"use client";

import { c as _c } from "react/compiler-runtime";
import type { PortalNavItem, PortalNavScope, PortalNavSection } from "@/lib/navigation/portal-nav";
import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useAuth } from "@/components/AuthProvider";
import { AwsMark } from "@/components/brand/aws-mark";
import { GlobalSearchTrigger } from "@/components/global-search-trigger";
import { usePortalNavFacts } from "@/components/portal-nav-provider";
import { PORTAL_SIDEBAR_CLASS } from "@/components/portal-shell-style";
import { SidebarUserMenu } from "@/components/SidebarUserMenu";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, useSidebar } from "@/components/ui/sidebar";
import { useConsoleClientNavigation } from "@/components/use-console-client-navigation";
import { buildPortalNav, isNavItemActive } from "@/lib/navigation/portal-nav";
import { GLOBAL_ROUTES, parseConsolePath } from "@/lib/navigation/portal-routes";
import { cn } from "@/lib/utils";
import { ArrowLeft, ArrowLeftRight, ArrowUpRight, Boxes, Building2, ChartNoAxesColumn, ChevronDown, FlaskConical, LifeBuoy, MessageSquare, Server, SquareCode, Wallet } from "lucide-react";
import { useTranslations } from "next-intl";
const OPEN_SECTIONS_KEY = "aireserve.portal-nav.sections.v3";
// The AWS Hub opens by default: a closed section hides the AWS mark that tells people where their account lives.
const DEFAULT_OPEN_SECTIONS = ["inference", "compute", "aws", "developers"];
const SECTION_ICONS: Record<string, React.ComponentType<{
  className?: string;
}>> = {
  inference: Boxes,
  compute: Server,
  aws: AwsMark,
  lab: FlaskConical,
  chat: MessageSquare,
  developers: SquareCode,
  observe: ChartNoAxesColumn,
  billing: Wallet,
  transfer: ArrowLeftRight,
  organization: Building2,
  help: LifeBuoy,
  platform: Boxes
};

/**
 * The one portal sidebar (portal IA overhaul, 2026-09) — for members,
 * enterprise admins and super admins alike. It renders the taxonomy from
 * `lib/navigation/portal-nav.ts` grouped by section, replacing both the old
 * global action bar (PrimaryNav) and the admin console's inner rail
 * (AdminNav). Which entries appear, and where they point, is decided by the
 * model from the viewer's role, the org's capabilities, and — for super
 * admins — whether the current URL is inside a client console.
 */
export function PortalSidebar() {
  const $ = _c(19);
  const pathname = usePathname();
  const {
    user
  } = useAuth();
  const facts = usePortalNavFacts();
  const t = useTranslations("sidebar");
  let t0;
  if ($[0] !== t) {
    t0 = (item, section) => section.id === "platform" ? item.label : t(`nav.${item.id}`);
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const labelFor = t0;
  const consoleMatch = facts.role === "super_admin" ? parseConsolePath(pathname) : null;
  const consoleClient = useConsoleClientNavigation(consoleMatch?.clientId ?? null);
  if (!user) {
    return null;
  }
  const scope = facts.role === "super_admin" ? consoleMatch ? {
    kind: "console",
    clientId: consoleMatch.clientId,
    clientName: consoleClient?.name ?? null
  } : {
    kind: "platform"
  } : {
    kind: "org"
  };
  const sections = buildPortalNav({
    role: facts.role,
    scope,
    chatEnabled: facts.chatEnabled,
    chatHref: facts.chatHref,
    voiceEnabled: consoleMatch ? consoleClient?.voiceEnabled ?? false : facts.voiceEnabled,
    fundingCodesEnabled: facts.fundingCodesEnabled,
    clientInvitesEnabled: facts.clientInvitesEnabled
  });
  const t1 = facts.orgName;
  let t2;
  if ($[2] !== t) {
    t2 = t("allClients");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t) {
    t3 = t("platform");
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = {
      allClients: t2,
      platform: t3
    };
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const T0 = SidebarContent;
  const t5 = "px-2 py-2";
  const t6 = "Portal sections";
  const t7 = "flex flex-col gap-1.5";
  let t8;
  if ($[9] !== labelFor || $[10] !== pathname || $[11] !== t) {
    t8 = section_0 => section_0.standalone ? <NavEntry key={section_0.id} item={section_0.items[0]} label={labelFor(section_0.items[0], section_0)} pathname={pathname} topLevel={true} /> : <NavSection key={section_0.id} section={section_0} pathname={pathname} label={t(`sections.${section_0.id}`)} labelFor={item_0 => labelFor(item_0, section_0)} />;
    $[9] = labelFor;
    $[10] = pathname;
    $[11] = t;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const t9 = sections.map(t8);
  let t10;
  if ($[13] !== t9) {
    t10 = <nav aria-label={t6} className={t7}>{t9}</nav>;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== T0 || $[16] !== t10) {
    t11 = <T0 className={t5}>{t10}</T0>;
    $[15] = T0;
    $[16] = t10;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <SidebarFooter className="gap-2 px-3 pt-2 pb-3"><GlobalSearchTrigger className="h-9 rounded-full border-white/60 bg-white/45 px-3 text-[#796a74] shadow-[inset_0_1px_0_rgba(255,255,255,0.85)] backdrop-blur-xl hover:bg-white/60 hover:text-[#332b32] dark:border-white/10 dark:bg-white/[0.06] dark:text-[#b7a9b2] dark:hover:bg-white/[0.10] dark:hover:text-[#f7f3f0]" /><SidebarUserMenu /></SidebarFooter>;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  return <Sidebar collapsible="offcanvas" variant="floating" className={PORTAL_SIDEBAR_CLASS} data-testid="portal-sidebar"><SidebarHeader className="px-4 pt-5 pb-3"><ScopeHeader scope={scope} orgName={t1} labels={t4} /></SidebarHeader>{t11}{t12}</Sidebar>;
}

/** Org name for members and admins; the client (with a way back) inside a console; "Platform" otherwise. */
function ScopeHeader(t0) {
  const $ = _c(12);
  const {
    scope,
    orgName,
    labels
  } = t0;
  if (scope.kind === "console") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <ArrowLeft className="h-3 w-3" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== labels.allClients) {
      t2 = <Link href={GLOBAL_ROUTES.clients} className="text-muted-foreground hover:text-foreground mb-0.5 flex items-center gap-1 text-xs transition-colors">{t1}{labels.allClients}</Link>;
      $[1] = labels.allClients;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    const t3 = scope.clientName ?? undefined;
    const t4 = scope.clientName ?? "\xA0";
    let t5;
    if ($[3] !== t3 || $[4] !== t4) {
      t5 = <p className="truncate text-base font-semibold tracking-tight" title={t3} data-testid="sidebar-scope-name">{t4}</p>;
      $[3] = t3;
      $[4] = t4;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    let t6;
    if ($[6] !== t2 || $[7] !== t5) {
      t6 = <div className="min-w-0">{t2}{t5}</div>;
      $[6] = t2;
      $[7] = t5;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    return t6;
  }
  const label = scope.kind === "platform" ? labels.platform : orgName;
  let t1;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span aria-hidden={true} className="size-2.5 shrink-0 rotate-45 rounded-[3px] border border-[#48333f] bg-[#6c2c59] shadow-[0_4px_12px_rgba(108,44,89,0.35)]" />;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const t2 = label ?? "\xA0";
  let t3;
  if ($[10] !== t2) {
    t3 = <div className="flex min-w-0 items-center gap-2.5">{t1}<p className="min-w-0 truncate text-sm font-medium tracking-[-0.01em]" data-testid="sidebar-scope-name">{t2}</p></div>;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}
function NavSection({
  section,
  pathname,
  label,
  labelFor
}: {
  section: PortalNavSection;
  pathname: string;
  label: string;
  labelFor: (item: PortalNavItem) => string;
}) {
  const active = section.items.some(item => isNavItemActive(item, pathname));
  const [open, setOpen] = useState(() => active || DEFAULT_OPEN_SECTIONS.includes(section.id));
  const Icon = SECTION_ICONS[section.id] ?? Boxes;

  // The section holding the current page always opens so the active entry
  // is visible; the remembered collapse applies everywhere else. Collapsing
  // it while on one of its pages still sticks — this only re-runs when
  // `active` flips, i.e. on navigating into (or out of) the section.
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(OPEN_SECTIONS_KEY) ?? "null") as Record<string, boolean> | null;
      setOpen(active || (stored?.[section.id] ?? DEFAULT_OPEN_SECTIONS.includes(section.id)));
    } catch {
      setOpen(active || DEFAULT_OPEN_SECTIONS.includes(section.id));
    }
  }, [active, section.id]);
  function toggle() {
    const next = !open;
    setOpen(next);
    try {
      const stored_0 = JSON.parse(localStorage.getItem(OPEN_SECTIONS_KEY) ?? "{}") as Record<string, boolean>;
      localStorage.setItem(OPEN_SECTIONS_KEY, JSON.stringify({
        ...stored_0,
        [section.id]: next
      }));
    } catch {
      // Navigation still works when storage is unavailable.
    }
  }
  return <section data-section={section.id} data-state={open ? "open" : "closed"} className={cn(section.id === "chat" && "my-1")}>
      <button type="button" onClick={toggle} aria-expanded={open} className={cn("flex w-full items-center gap-2.5 rounded-full px-3 py-2 text-left text-[13px] font-medium tracking-[-0.01em] text-[#493e46] transition-colors hover:bg-white/45 dark:text-[#e9e1e6] dark:hover:bg-white/[0.07]")}>
        <Icon className="size-4 shrink-0" />
        <span className="min-w-0 flex-1 leading-tight">{label}</span>
        <ChevronDown className={cn("size-3.5 text-[#8b7d86] transition-transform dark:text-[#9f8f99]", !open && "-rotate-90")} />
      </button>
      {open && <div className="mt-0.5 ml-3 grid gap-0.5 pl-3" data-testid={`sidebar-section-${section.id}-items`}>
          {section.items.map(item_0 => <NavEntry key={item_0.id} item={item_0} label={labelFor(item_0)} pathname={pathname} />)}
        </div>}
    </section>;
}
function NavEntry(t0) {
  const $ = _c(12);
  const {
    item,
    label,
    pathname,
    topLevel: t1
  } = t0;
  const topLevel = t1 === undefined ? false : t1;
  const Icon = item.icon;
  const active = isNavItemActive(item, pathname);
  const {
    isMobile,
    setOpenMobile
  } = useSidebar();
  let t2;
  if ($[0] !== isMobile || $[1] !== setOpenMobile) {
    t2 = () => {
      if (isMobile) {
        setOpenMobile(false);
      }
    };
    $[0] = isMobile;
    $[1] = setOpenMobile;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const closeMobile = t2;
  if (!item.href) {
    return null;
  }
  const className = cn("flex items-center gap-2.5 rounded-full px-3 py-2 text-[13px] text-[#796a74] transition-[background-color,color,transform] hover:translate-x-0.5 hover:bg-white/45 hover:text-[#332b32] dark:text-[#b7a9b2] dark:hover:bg-white/[0.07] dark:hover:text-[#f7f3f0]", active && "bg-[#6c2c59] font-medium text-[#f7f7f7] shadow-[0_9px_22px_-13px_rgba(108,44,89,0.9),inset_0_1px_0_rgba(255,255,255,0.14)] hover:bg-[#763260] hover:text-white dark:bg-[#793465] dark:text-white dark:hover:bg-[#84406f]", topLevel && !active && "text-sm font-medium text-[#493e46] dark:text-[#e9e1e6]");
  let t3;
  if ($[3] !== Icon || $[4] !== active || $[5] !== className || $[6] !== closeMobile || $[7] !== item.external || $[8] !== item.href || $[9] !== label || $[10] !== topLevel) {
    t3 = item.external ? <a href={item.href} target="_blank" rel="noopener noreferrer" className={className} onClick={closeMobile}><Icon className={cn("size-3.5 shrink-0", topLevel && "size-4")} /><span className="min-w-0 flex-1 leading-tight">{label}</span><ArrowUpRight className="text-muted-foreground size-3" /></a> : <Link href={item.href} aria-current={active ? "page" : undefined} className={className} onClick={closeMobile}><Icon className={cn("size-3.5 shrink-0", topLevel && "size-4")} /><span className="min-w-0 flex-1 leading-tight">{label}</span></Link>;
    $[3] = Icon;
    $[4] = active;
    $[5] = className;
    $[6] = closeMobile;
    $[7] = item.external;
    $[8] = item.href;
    $[9] = label;
    $[10] = topLevel;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}